import React from "react";
import KuikaBadge, { type KuikaBadgeColor } from "../../ui-kit/kuika-badge/kuika-badge";
import KuikaButton from "../../ui-kit/kuika-button/kuika-button";
import { EntityDiffCode } from "./entity-diff-code";
import type { EntityBadgeHue, EntityDiffField, ParamRow, KeyValuePair } from "./entity-diff-types";
import "./entity-diff.scss";

/** Shared semantic tone vocabulary for this diff surface (badges, cell highlights, summary highlights). */
export type EntityDiffTone = "added" | "removed" | "info" | "warning";

interface EntityDiffFieldRowProps {
  field: EntityDiffField;
  mode: "staged" | "merge";
  isResolved?: boolean;
  resolvedChoice?: "ours" | "theirs" | "base";
  onResolve?: (choice: "ours" | "theirs" | "base") => void;
  /** Branch names for target ("ours") and source ("theirs") so buttons read in source/target terms. */
  targetLabel?: string;
  sourceLabel?: string;
}

/** Maps the internal git-style choice to the user-facing source/target vocabulary. */
function choiceWord(choice: "ours" | "theirs" | "base"): string {
  if (choice === "ours") return "target";
  if (choice === "theirs") return "source";
  return "base";
}

interface ChangeBadgeConfig {
  label: string;
  color: KuikaBadgeColor;
}

const CHANGE_BADGES: Record<string, ChangeBadgeConfig> = {
  added: { label: "Added", color: "green" },
  removed: { label: "Removed", color: "red" },
  modified: { label: "Modified", color: "blue" },
  conflict: { label: "Conflict", color: "yellow" }
};

interface ChangeBadgeProps {
  status: EntityDiffField["changeStatus"];
}

function ChangeBadge({ status }: ChangeBadgeProps): React.ReactElement | null {
  const badge: ChangeBadgeConfig | undefined = CHANGE_BADGES[status];
  if (!badge) return null;
  return <KuikaBadge type="label" size="small" color={badge.color} content={badge.label} />;
}

// ── Rich renderers for structured data ──

interface ParamsTableProps {
  rows: ParamRow[];
}

function ParamsTable({ rows }: ParamsTableProps): React.ReactElement {
  return (
    <table className="kuika-entity-diff__params-table">
      <thead>
        <tr>
          <th>Name</th>
          <th>Type</th>
          <th>Required</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i}>
            <td>
              <code className="kuika-entity-diff__param-name">{row.name}</code>
            </td>
            <td>
              <span className="kuika-entity-diff__param-type">{row.type || "--"}</span>
            </td>
            <td>
              {row.required ? (
                <span className="kuika-entity-diff__param-flag" data-required="true">
                  Required
                </span>
              ) : (
                <span className="kuika-entity-diff__param-flag">Optional</span>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

interface KeyValueTableProps {
  pairs: KeyValuePair[];
}

function KeyValueTable({ pairs }: KeyValueTableProps): React.ReactElement {
  return (
    <div className="kuika-entity-diff__kv">
      {pairs.map((pair, i) => (
        <React.Fragment key={i}>
          <span className="kuika-entity-diff__kv-key">{pair.key}</span>
          <span className="kuika-entity-diff__kv-value" data-masked={pair.masked || undefined}>
            {pair.value}
          </span>
        </React.Fragment>
      ))}
    </div>
  );
}

interface ListItemsProps {
  items: string[];
}

function ListItems({ items }: ListItemsProps): React.ReactElement {
  return (
    <div className="kuika-entity-diff__list">
      {items.map((item, i) => (
        <div key={i} className="kuika-entity-diff__list-item">
          <span className="kuika-entity-diff__bullet">&#x25CF;</span>
          <span>{item}</span>
        </div>
      ))}
    </div>
  );
}

interface BadgeValueProps {
  value: string;
  hue?: EntityBadgeHue;
}

/**
 * Free-form value badge (table name, status text, …). Unlike `ChangeBadge` this has no fixed
 * semantic hue to draw from — the caller may optionally mark it with one of the four shared
 * tones; otherwise it renders the neutral/default look.
 */
function BadgeValue({ value, hue }: BadgeValueProps): React.ReactElement {
  return (
    <span className="kuika-entity-diff__badge" data-hue={hue}>
      {value}
    </span>
  );
}

// ── Value cell: dispatches to the right renderer ──

interface ValueCellProps {
  value?: string;
  renderType?: EntityDiffField["renderType"];
  language?: string;
  compareValue?: string;
  highlightTone?: EntityDiffTone;
  structuredData?: ParamRow[] | KeyValuePair[] | string[];
  codeTone?: "added" | "removed";
  badgeHue?: EntityBadgeHue;
}

function ValueCell({
  value,
  renderType,
  language,
  compareValue,
  highlightTone,
  structuredData,
  codeTone,
  badgeHue
}: ValueCellProps): React.ReactElement {
  if (!value && !structuredData) return <span className="kuika-entity-diff__empty-value">--</span>;

  const wrapper = (children: React.ReactElement): React.ReactElement =>
    highlightTone ? (
      <div className="kuika-entity-diff__highlight" data-tone={highlightTone}>
        {children}
      </div>
    ) : (
      children
    );

  if (renderType === "code") {
    return wrapper(
      <EntityDiffCode value={value ?? ""} language={language} compareValue={compareValue} tone={codeTone} />
    );
  }

  if (renderType === "params-table" && structuredData && isParamRows(structuredData)) {
    return wrapper(<ParamsTable rows={structuredData} />);
  }

  if (renderType === "key-value" && structuredData && isKeyValuePairs(structuredData)) {
    return wrapper(<KeyValueTable pairs={structuredData} />);
  }

  if (renderType === "list" && structuredData && isStringArray(structuredData)) {
    return wrapper(<ListItems items={structuredData} />);
  }

  if (renderType === "badge") {
    return wrapper(<BadgeValue value={value ?? ""} hue={badgeHue} />);
  }

  // Default: text
  return wrapper(<div className="kuika-entity-diff__value-text">{value}</div>);
}

function isParamRows(data: unknown[]): data is ParamRow[] {
  return (
    data.length > 0 && typeof data[0] === "object" && data[0] !== null && "name" in (data[0] as Record<string, unknown>)
  );
}

function isKeyValuePairs(data: unknown[]): data is KeyValuePair[] {
  return (
    data.length > 0 &&
    typeof data[0] === "object" &&
    data[0] !== null &&
    "key" in (data[0] as Record<string, unknown>) &&
    "value" in (data[0] as Record<string, unknown>)
  );
}

function isStringArray(data: unknown[]): data is string[] {
  return data.length > 0 && typeof data[0] === "string";
}

// ── Main field row component ──

export function EntityDiffFieldRow({
  field,
  mode,
  isResolved,
  resolvedChoice,
  onResolve,
  targetLabel,
  sourceLabel
}: EntityDiffFieldRowProps): React.ReactElement {
  if (mode === "staged") {
    return (
      <div className="kuika-entity-diff__field-row">
        <div className="kuika-entity-diff__field-label-wrap">
          <span className="kuika-entity-diff__field-label">{field.label}</span>
          <ChangeBadge status={field.changeStatus} />
        </div>
        <ValueCell
          value={field.beforeValue}
          renderType={field.renderType}
          badgeHue={field.badgeHue}
          language={field.language}
          compareValue={field.afterValue}
          codeTone="removed"
          structuredData={field.beforeData}
          highlightTone={field.changeStatus === "removed" || field.changeStatus === "modified" ? "removed" : undefined}
        />
        <ValueCell
          value={field.afterValue}
          renderType={field.renderType}
          badgeHue={field.badgeHue}
          language={field.language}
          compareValue={field.beforeValue}
          codeTone="added"
          structuredData={field.afterData}
          highlightTone={field.changeStatus === "added" || field.changeStatus === "modified" ? "added" : undefined}
        />
      </div>
    );
  }

  // Merge mode
  const showResolution: boolean = Boolean(field.changeStatus === "conflict" && onResolve);

  return (
    <div
      className="kuika-entity-diff__field-row kuika-entity-diff__field-row--merge"
      data-status={field.changeStatus}
      data-resolved={isResolved || undefined}
    >
      <div className="kuika-entity-diff__field-label-wrap">
        <span className="kuika-entity-diff__field-label kuika-entity-diff__field-label--bold">{field.label}</span>
        <ChangeBadge status={isResolved ? "modified" : field.changeStatus} />
        {isResolved && (
          <span className="kuika-entity-diff__resolved-label">
            Resolved ({resolvedChoice ? choiceWord(resolvedChoice) : ""})
          </span>
        )}
      </div>

      <div className="kuika-entity-diff__merge-cols">
        <div>
          <div className="kuika-entity-diff__merge-col-label" data-role="base">
            Base
          </div>
          <ValueCell
            value={field.baseValue}
            renderType={field.renderType}
            badgeHue={field.badgeHue}
            language={field.language}
            structuredData={field.baseData}
          />
        </div>
        <div>
          <div className="kuika-entity-diff__merge-col-label" data-role="ours">
            Target{targetLabel ? ` · ${targetLabel}` : ""}
          </div>
          <ValueCell
            value={field.oursValue}
            renderType={field.renderType}
            badgeHue={field.badgeHue}
            language={field.language}
            structuredData={field.oursData}
            highlightTone={field.baseValue !== field.oursValue ? "info" : undefined}
          />
        </div>
        <div>
          <div className="kuika-entity-diff__merge-col-label" data-role="theirs">
            Source{sourceLabel ? ` · ${sourceLabel}` : ""}
          </div>
          <ValueCell
            value={field.theirsValue}
            renderType={field.renderType}
            badgeHue={field.badgeHue}
            language={field.language}
            structuredData={field.theirsData}
            highlightTone={field.baseValue !== field.theirsValue ? "warning" : undefined}
          />
        </div>
      </div>

      {showResolution && (
        <div className="kuika-entity-diff__resolve-actions">
          <KuikaButton
            type="secondary"
            size="sm"
            active={resolvedChoice === "ours"}
            onClick={() => onResolve?.("ours")}
          >
            Use target{targetLabel ? ` (${targetLabel})` : ""}
          </KuikaButton>
          <KuikaButton
            type="secondary"
            size="sm"
            active={resolvedChoice === "theirs"}
            onClick={() => onResolve?.("theirs")}
          >
            Use source{sourceLabel ? ` (${sourceLabel})` : ""}
          </KuikaButton>
        </div>
      )}
    </div>
  );
}
